import React, { useEffect, useState, useCallback } from "react";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useSettings } from "@/contexts/SettingsContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  CheckCircle2, XCircle, Users as UsersIcon, ArrowDownToLine,
  ArrowUpFromLine, ShieldCheck, Search, Shield, MessageCircle,
  Gift, Plus, X, ToggleLeft, ToggleRight, Send, Trash2, Settings,
  BarChart3, Bell, Ban, Unlock, SlidersHorizontal, TrendingUp,
  DollarSign, Activity, UserPlus, Eye, AlertTriangle, History,
  TrendingDown, Zap, Filter, RefreshCw, ChevronDown, ChevronUp,
  ClipboardList, FileDown, FileText, Download, UserCheck
} from "lucide-react";

// ═══════════════════════════════════════════════════════════
//  MODAL COMPONENTS
// ═══════════════════════════════════════════════════════════

function ModalBase({ open, onClose, title, icon: Icon, iconColor = "var(--iq-green)", children, maxW = "max-w-md" }) {
  const { t } = useI18n();
  useEffect(() => {
    const onKey = (e) => e.key === t("escape") && onClose();
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, t]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className={`relative z-10 w-full ${maxW} bg-panel border border-line rounded-lg shadow-2xl`}
        style={{ boxShadow: "0 0 40px rgba(0,230,118,0.08)" }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <div className="flex items-center gap-2 font-semibold text-sm">
            {Icon && <Icon className="w-4 h-4" style={{ color: iconColor }} />}
            {title}
          </div>
          <button onClick={onClose} className="text-dim hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-5">{children}</div>
      </div>
    </div>
  );
}

function ConfirmModal({ open, onClose, onConfirm, title, message, confirmLabel, confirmColor = "bg-sell text-white hover:bg-red-500", icon, loading }) {
  const { t } = useI18n();
  return (
    <ModalBase open={open} onClose={onClose} title={title} icon={icon || AlertTriangle} iconColor="#ef4444">
      <p className="text-sm text-muted mb-6 leading-relaxed">{message}</p>
      <div className="flex gap-2 justify-end">
        <button onClick={onClose} className="px-4 py-2 border border-line rounded-sm text-xs text-dim hover:bg-panel-hover uppercase tracking-widest">
          t("cancel")
        </button>
        <button onClick={onConfirm} disabled={loading}
          className={`px-5 py-2 rounded-sm text-xs font-bold uppercase tracking-widest disabled:opacity-50 ${confirmColor}`}>
          {loading ? t("processing") : (confirmLabel || t("confirm"))}
        </button>
      </div>
    </ModalBase>
  );
}

function InputModal({ open, onClose, onSubmit, title, icon, fields, confirmLabel, confirmColor = "bg-buy text-black hover:bg-emerald-400" }) {
  const { t } = useI18n();
  const [vals, setVals] = useState({});
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (open) {
      const init = {};
      (fields || []).forEach(f => { init[f.key] = f.default ?? ""; });
      setVals(init);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    setLoading(true);
    try { await onSubmit(vals); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <ModalBase open={open} onClose={onClose} title={title} icon={icon}>
      <div className="space-y-4 mb-6">
        {(fields || []).map(f => (
          <div key={f.key}>
            <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">{f.label}{f.required && " *"}</label>
            {f.type === "textarea" ? (
              <textarea value={vals[f.key] ?? ""} onChange={e => setVals(v => ({ ...v, [f.key]: e.target.value }))}
                rows={3} placeholder={f.placeholder}
                className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy resize-none" />
            ) : f.type === "select" ? (
              <select value={vals[f.key] ?? ""} onChange={e => setVals(v => ({ ...v, [f.key]: e.target.value }))}
                className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy">
                {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <input type={f.type || "text"} value={vals[f.key] ?? ""} onChange={e => setVals(v => ({ ...v, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy font-mono" />
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onClose} className="px-4 py-2 border border-line rounded-sm text-xs text-dim hover:bg-panel-hover uppercase tracking-widest">
          t("cancel")
        </button>
        <button onClick={submit} disabled={loading}
          className={`px-5 py-2 rounded-sm text-xs font-bold uppercase tracking-widest disabled:opacity-50 flex items-center gap-2 ${confirmColor}`}>
          {loading ? t("processing") : (confirmLabel || t("send"))}
        </button>
      </div>
    </ModalBase>
  );
}

// ═══════════════════════════════════════════════════════════
//  MAIN ADMIN COMPONENT
// ═══════════════════════════════════════════════════════════
export default function Admin() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [kyc, setKyc] = useState([]);
    const [kycFilter, setKycFilter] = useState("pending");
  const [users, setUsers] = useState([]);
  const [userQ, setUserQ] = useState("");
  const [tickets, setTickets] = useState([]);
  const [promos, setPromos] = useState([]);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [financialReport, setFinancialReport] = useState(null);
  const [reportPeriod, setReportPeriod] = useState("monthly");
  const [userDetail, setUserDetail] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Modal state
  const [modal, setModal] = useState(null); // { type, data }

  const isSuperadmin = user?.role === "superadmin";

  const closeModal = useCallback(() => setModal(null), []);

  const load = async () => {
    try {
      const [s, tx, kk, uu, pm] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/transactions?status=pending"),
        api.get(`/admin/kyc?status=${kycFilter}`),
        api.get("/admin/users", { params: { limit: 200, q: userQ || undefined } }),
        api.get("/admin/payment-methods")
      ]);
      setStats(s.data ?? {});
      const txArr = Array.isArray(tx.data) ? tx.data : [];
      setDeposits(txArr.filter((x) => x.type === "deposit"));
      setWithdrawals(txArr.filter((x) => x.type === "withdraw"));
      setKyc(Array.isArray(kk.data) ? kk.data : []);
      setUsers(Array.isArray(uu.data) ? uu.data : []);
      setPaymentMethods(Array.isArray(pm.data) ? pm.data : []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadAnalytics = async () => {
    try { const { data } = await api.get("/admin/analytics?days=30"); setAnalytics(data ?? {}); } catch {}
  };
  const loadTickets = async () => {
    try { const { data } = await api.get("/support/tickets"); setTickets(Array.isArray(data) ? data : []); } catch { setTickets([]); }
  };
  const loadPromos = async () => {
    try { const { data } = await api.get("/support/admin/promos"); setPromos(Array.isArray(data) ? data : []); } catch { setPromos([]); }
  };
  const loadKyc = async (filter) => {
    try {
      const { data } = await api.get(`/admin/kyc?status=${filter ?? kycFilter}`);
      setKyc(Array.isArray(data) ? data : []);
    } catch (e) { setKyc([]); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "superadmin")) {
      load(); loadAnalytics(); loadTickets(); loadPromos();
    }
  }, [user]); // eslint-disable-line

  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "superadmin")) {
      loadKyc();
    }
  }, [kycFilter]); // eslint-disable-line

  if (user && user.role !== "admin" && user.role !== "superadmin") return <Navigate to="/trade" replace />;

  const approveTx = async (id) => { try { await api.post(`/admin/transactions/${id}/approve`); toast.success(t("toast_admin_approved")); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const rejectTx  = async (id) => { try { await api.post(`/admin/transactions/${id}/reject`);  toast.success(t("toast_admin_rejected")); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const approveKyc = async (id) => { try { await api.post(`/admin/kyc/${id}/approve`); toast.success(t("toast_kyc_approved")); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  // --- Modal-based actions ---
  const rejectKyc = (id) => setModal({ type: "rejectKyc", id });
  const setUserRole = (uid, currentRole) => setModal({ type: "setRole", uid, currentRole });
  const adjustBalance = (uid) => setModal({ type: "balance", uid });
  const banUser = (uid, email) => setModal({ type: "ban", uid, email });
  const unbanUser = (uid, email) => setModal({ type: "unban", uid, email });
  const setUserLimits = (uid, limits) => setModal({ type: "limits", uid, limits });
  const replyTicket = (id) => setModal({ type: "reply", id });

  // Execute modal actions
  const execRejectKyc = async ({ reason }) => {
    const fd = new FormData(); fd.append("reason", reason || t("insufficient_docs"));
    await api.post(`/admin/kyc/${modal.id}/reject`, fd);
    toast.success(t("toast_kyc_rejected")); load();
  };
  const execSetRole = async ({ role }) => {
    await api.post(`/admin/users/${modal.uid}/role`, { role });
    toast.success(t("toast_role_updated")); load();
  };
  const execBalance = async ({ mode, amount }) => {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt === 0) throw new Error(t("enter_valid_amount"));
    await api.post(`/admin/users/${modal.uid}/balance`, { mode, amount: amt });
    toast.success(t("toast_balance_updated")); load();
  };
  const execBan = async ({ reason, duration_days }) => {
    const days = duration_days ? Number(duration_days) : null;
    await api.post(`/admin/users/${modal.uid}/ban`, { reason: reason || t("suspicious_activity"), duration_days: days });
    toast.success(days ? days + " " + t("toast_banned_days") : t("toast_banned_permanent")); load();
  };
  const execUnban = async () => {
    await api.post(`/admin/users/${modal.uid}/unban`);
    toast.success(t("toast_block_removed")); load(); closeModal();
  };
  const execLimits = async ({ max_trade_amount, max_daily_trades, max_daily_withdrawal }) => {
    const limits = {};
    if (max_trade_amount) limits.max_trade_amount = Number(max_trade_amount);
    if (max_daily_trades) limits.max_daily_trades = Number(max_daily_trades);
    if (max_daily_withdrawal) limits.max_daily_withdrawal = Number(max_daily_withdrawal);
    if (Object.keys(limits).length === 0) {
      await api.delete(`/admin/users/${modal.uid}/limits`);
      toast.success(t("toast_limits_removed"));
    } else {
      await api.post(`/admin/users/${modal.uid}/limits`, limits);
      toast.success(t("toast_limits_updated"));
    }
    load();
  };
  const execReply = async ({ message }) => {
    if (!message?.trim()) throw new Error(t("message_cannot_be_empty"));
    await api.post(`/support/tickets/${modal.id}/reply`, { message });
    toast.success(t("toast_reply_sent")); loadTickets();
  };

  const closeTicket = async (id) => setModal({ type: "closeTicket", id });
  const execCloseTicket = async () => {
    await api.post(`/support/tickets/${modal.id}/close`);
    toast.success(t("toast_ticket_closed")); loadTickets(); closeModal();
  };

  const togglePromo = async (id) => { try { await api.patch(`/support/admin/promos/${id}/toggle`); toast.success(t("toast_updated")); loadPromos(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const deletePromo = (id) => setModal({ type: "deletePromo", id });
  const execDeletePromo = async () => {
    await api.delete(`/support/admin/promos/${modal.id}`);
    toast.success(t("toast_promo_deleted")); loadPromos(); closeModal();
  };

  const openTickets = tickets.filter(t => t.status === "open").length;

  return (
    <div className="min-h-screen bg-app text-white">
      <TopBar />
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-6">
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight mb-4">t("admin")</h1>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-line mb-4">
          <Stat icon={UsersIcon} label={t("users")} value={stats?.users ?? "—"} />
          <Stat icon={ShieldCheck} label={t("open_trades")} value={stats?.open_trades ?? "—"} />
          <Stat icon={ArrowDownToLine} label={t("pending_deposits")} value={deposits.length} />
          <Stat icon={ArrowUpFromLine} label={t("pending_withdrawals")} value={withdrawals.length} />
          <Stat icon={MessageCircle} label={t("support_tickets")} value={openTickets} />
        </div>

        {/* Main Content Layout */}
        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <div className="w-full md:w-56 shrink-0 flex flex-col gap-1">
            {[
              { k: "dashboard",    l: t("dashboard"),           n: "",              icon: BarChart3 },
              { k: "deposits",     l: t("pending_deposits"), n: deposits.length },
              { k: "withdrawals",  l: t("pending_withdrawals"), n: withdrawals.length },
                            { k: "kyc",          l: t("kyc_approvals"),        n: kyc.filter(k => k.status === "pending").length || "" },
              { k: "users",        l: t("users"),               n: users.length },
              { k: "trades",       l: t("trades"),            n: "",              icon: TrendingUp },
              { k: "txhistory",    l: t("tx_history"),           n: "",              icon: History },
              { k: "notifications",l: t("notifications_label"),         n: "",              icon: Bell },
              { k: "support",      l: t("support"),              n: openTickets,     icon: MessageCircle },
              { k: "promos",       l: t("promotions"),        n: promos.length,   icon: Gift },
              { k: "payment_methods", l: t("payment_methods"), n: paymentMethods.length, icon: DollarSign },
              { k: "settings",     l: t("general_settings"),       n: "",              icon: Settings },
              { k: "audit-logs",   l: t("audit_log"),        n: "",              icon: ClipboardList },
              { k: "reports",      l: t("financial_reports"),   n: "",              icon: FileText },
            ].map((x) => (
              <button key={x.k} data-testid={`admin-tab-${x.k}`} onClick={() => setTab(x.k)}
                className={`px-4 py-3 text-[10px] uppercase tracking-widest font-semibold flex items-center justify-between rounded-sm transition-colors text-left ${
                  tab === x.k 
                    ? "bg-panel border-l-2 border-buy text-white" 
                    : "text-muted hover:bg-panel-hover hover:text-white border-l-2 border-transparent"
                }`}>
                <div className="flex items-center gap-2">
                  {x.icon && <x.icon className="w-3.5 h-3.5" />}
                  {x.l}
                </div>
                {x.n !== "" && x.n > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-mono ${tab === x.k ? "bg-buy text-black" : "bg-line text-dim"}`}>{x.n}</span>
                )}
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className="flex-1 min-w-0">

        {/* ─── Dashboard Tab ─── */}
        {tab === "dashboard" && <DashboardTab analytics={analytics} />}

        {tab === "deposits" && (
          <TxTable rows={deposits} onApprove={approveTx} onReject={rejectTx} testid="deposits-table" showHash />
        )}
        {tab === "withdrawals" && (
          <TxTable rows={withdrawals} onApprove={approveTx} onReject={rejectTx} testid="withdrawals-table" showAddress />
        )}
        {tab === "kyc" && (
          <div className="space-y-4">
            {/* KYC Status Filter Tabs */}
            <div className="flex gap-2 flex-wrap items-center">
              {[
                { key: "pending",  label: t("pending"),   color: "text-yellow-400" },
                { key: "approved", label: t("approved_plural"),  color: "text-buy" },
                { key: "rejected", label: t("rejected_plural"), color: "text-sell" },
                { key: "all",      label: t("all"),       color: "text-white" },
              ].map(f => (
                <button key={f.key}
                  onClick={() => setKycFilter(f.key)}
                  className={`px-4 py-2 text-[10px] uppercase tracking-widest font-semibold rounded-sm border transition-colors ${
                    kycFilter === f.key
                      ? "bg-panel border-buy text-white"
                      : "border-line text-dim hover:bg-panel-hover hover:text-white"
                  }`}>
                  <span className={kycFilter === f.key ? f.color : ""}>{f.label}</span>
                </button>
              ))}
              <div className="ml-auto">
                <CsvButton endpoint="/admin/export/kyc" filename="kyc.csv" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {kyc.length === 0 && <div className="text-dim text-sm col-span-full text-center py-12">Kayıtlı KYC evrağı bulunamadı</div>}
              {kyc.map((k) => (
                <div key={k.id} data-testid={`kyc-${k.id}`} className="bg-panel border border-line rounded-sm p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold">{k.full_name}</div>
                      <div className="text-xs text-dim">{k.user_email}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className="text-[10px] uppercase tracking-widest text-buy">{k.document_type.replace("_", " ")}</div>
                      {k.status !== "pending" && (
                        <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm border ${
                          k.status === "approved"
                            ? "text-buy border-buy/30 bg-buy/10"
                            : "text-sell border-sell/30 bg-sell/10"
                        }`}>
                          {k.status === "approved" ? t("approved") : t("rejected") }
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                    <div><span className="text-dim">DOB:</span> {k.date_of_birth}</div>
                    <div><span className="text-dim">Country:</span> {k.country}</div>
                  </div>
                  {k.data_base64 && (
                    <img src={`data:${k.content_type};base64,${k.data_base64}`} alt="doc"
                      className="w-full max-h-64 object-contain bg-app border border-line rounded-sm" />
                  )}
                  {k.status === "pending" ? (
                    <div className="flex gap-2">
                      <button data-testid={`kyc-approve-${k.id}`} onClick={() => approveKyc(k.id)}
                        className="flex-1 py-2 text-xs uppercase tracking-widest bg-buy text-black font-semibold rounded-sm hover:bg-emerald-400 flex items-center justify-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />t("approve")
                      </button>
                      <button data-testid={`kyc-reject-${k.id}`} onClick={() => rejectKyc(k.id)}
                        className="flex-1 py-2 text-xs uppercase tracking-widest bg-sell text-white font-semibold rounded-sm hover:bg-red-500 flex items-center justify-center gap-1">
                        <XCircle className="w-3.5 h-3.5" />t("reject")
                      </button>
                      <button onClick={async () => {
                        const note = prompt(t("reload_note"));
                        if (!note) return;
                        try {
                          await api.post(`/admin/kyc/${k.id}/request-resubmit`, { note });
                          toast.success(t("toast_reload_requested"));
                          loadKyc();
                        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                      }}
                        className="flex-1 py-2 text-xs uppercase tracking-widest border border-yellow-500 text-yellow-500 font-semibold rounded-sm hover:bg-yellow-900 flex items-center justify-center gap-1">
                        <RefreshCw className="w-3.5 h-3.5" /> Yeniden Talep
                      </button>
                    </div>
                  ) : (
                    <div className="text-center text-xs font-mono py-2 rounded-sm bg-app border border-line uppercase tracking-widest">
                      Durum: <span className={k.status === "approved" ? "text-buy" : "text-sell"}>{k.status === "approved" ? t("approved") : t("rejected") }</span>
                      {k.rejection_reason && <div className="text-[10px] text-sell mt-1 normal-case tracking-normal">Sebep: {k.rejection_reason}</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─── Users Tab ─── */}
        {tab === "users" && (
          <UsersTab
            users={users} userQ={userQ} setUserQ={setUserQ} load={load}
            isSuperadmin={isSuperadmin}
            setUserRole={setUserRole} adjustBalance={adjustBalance}
            banUser={banUser} unbanUser={unbanUser} setUserLimits={setUserLimits}
            setUserDetail={setUserDetail}
          />
        )}

        {/* ─── Trades Tab ─── */}
        {tab === "trades" && <TradesTab />}

        {/* ─── Tx History Tab ─── */}
        {tab === "txhistory" && <TxHistoryTab />}

        {/* ─── Notifications Tab ─── */}
        {tab === "notifications" && <NotificationsTab users={users} />}

        {/* ─── Support Tickets Tab ─── */}
        {tab === "support" && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              {["open", "answered", "closed"].map(s => (
                <div key={s} className="bg-panel border border-line rounded-sm px-4 py-3">
                  <div className="text-[10px] uppercase tracking-widest text-dim mb-1">
                    {s === "open" ? t("open_status") : s === "answered" ? t("answered") : t("closed_status") }
                  </div>
                  <div className="text-2xl font-bold font-mono" style={{ color: s === "open" ? "var(--iq-orange)" : s === "answered" ? "var(--iq-green)" : "#888" }}>
                    {tickets.filter(t => t.status === s).length}
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              {tickets.length === 0 && <div className="text-center py-12 text-dim text-sm">Destek talebi yok</div>}
              {tickets.map(ticket => (
                <div key={ticket.id} className="bg-panel border border-line rounded-sm p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold text-sm">{ticket.subject}</div>
                      <div className="text-xs text-dim">{ticket.user_email} · {new Date(ticket.created_at).toLocaleString("tr-TR")}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border font-semibold ${
                        ticket.status === "open" ? "border-orange-400 text-orange-400" :
                        ticket.status === "answered" ? "border-buy text-buy" : "border-line text-dim"
                      }`}>
                        {ticket.status === "open" ? t("open_status") : ticket.status === "answered" ? t("answered") : t("closed_status") }
                      </span>
                      <span className="text-[10px] text-dim border border-line rounded-sm px-2 py-0.5">{ticket.category}</span>
                    </div>
                  </div>
                  <div className="text-sm text-muted mb-3 bg-app rounded-sm p-3 border border-subtle">{ticket.message}</div>
                  {ticket.replies?.length > 0 && (
                    <div className="mb-3 space-y-2">
                      {ticket.replies.map((r, i) => (
                        <div key={i} className="bg-panel-hover border border-buy border-opacity-20 rounded-sm p-3">
                          <div className="text-[10px] text-buy font-semibold mb-1">Admin Yanıtı · {r.admin_email}</div>
                          <div className="text-sm text-white">{r.message}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {ticket.status !== "closed" && (
                    <div className="flex gap-2">
                      <button onClick={() => replyTicket(ticket.id)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-buy text-black text-xs font-semibold rounded-sm hover:bg-emerald-400 uppercase tracking-widest">
                        <Send className="w-3 h-3" /> Yanıtla
                      </button>
                      <button onClick={() => closeTicket(ticket.id)}
                        className="flex items-center gap-1 px-3 py-1.5 border border-line text-dim text-xs rounded-sm hover:bg-panel-hover uppercase tracking-widest">
                        <X className="w-3 h-3" /> Kapat
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─── Promotions Tab ─── */}
        {tab === "promos" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-muted">{promos.length} promosyon</div>
              <button onClick={() => setShowPromoForm(!showPromoForm)}
                className="flex items-center gap-2 px-4 py-2 bg-buy text-black text-xs font-bold rounded-sm hover:bg-emerald-400 uppercase tracking-widest">
                <Plus className="w-3.5 h-3.5" /> Yeni Promosyon
              </button>
            </div>
            {showPromoForm && <PromoForm onCreated={() => { setShowPromoForm(false); loadPromos(); }} onClose={() => setShowPromoForm(false)} />}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {promos.length === 0 && (
                <div className="col-span-full text-center py-12 text-dim text-sm">
                  <Gift className="w-10 h-10 mx-auto mb-3 opacity-30" /> Henüz promosyon yok
                </div>
              )}
              {promos.map(p => {
                const typeColor = { bonus: "#00e676", cashback: "#448aff", free_trade: "#ff9800", deposit_match: "#a855f7" }[p.promo_type] || "#888";
                const typeLabel = { bonus: t("bonus"), cashback: t("cashback"), free_trade: t("free_trade"), deposit_match: t("deposit_match") }[p.promo_type] || p.promo_type;
                return (
                  <div key={p.id} className="bg-panel border border-line rounded-sm p-4" style={{ borderLeft: `3px solid ${typeColor}44` }}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold">{p.title}</div>
                      <div className="flex items-center gap-2">
                        <span style={{ fontSize: 9, color: typeColor, background: `${typeColor}22`, border: `1px solid ${typeColor}44`, borderRadius: 3, padding: "2px 6px", fontWeight: 700, textTransform: "uppercase" }}>{typeLabel}</span>
                        <span className={`text-[10px] font-semibold ${p.is_active ? "text-buy" : "text-dim"}`}>{p.is_active ? "● AKTİF" : "○ PASİF"}</span>
                      </div>
                    </div>
                    <div className="text-xs text-muted mb-3">{p.description}</div>
                    <div className="flex justify-between items-center mb-3">
                      <div className="text-2xl font-mono font-black" style={{ color: typeColor }}>
                        +{p.value}{p.promo_type === "cashback" || p.promo_type === "deposit_match" ? "%" : "$"}
                      </div>
                      <div className="text-right">
                        {p.min_deposit > 0 && <div className="text-[10px] text-dim">Min. ${p.min_deposit}</div>}
                        <div className="text-[10px] text-dim">{p.use_count || 0} kullanım</div>
                        {p.max_uses && <div className="text-[10px] text-dim">Max: {p.max_uses}</div>}
                      </div>
                    </div>
                    {p.expires_at && <div className="text-[10px] text-dim mb-3">Son: {new Date(p.expires_at).toLocaleDateString("tr-TR")}</div>}
                    <div className="flex gap-2">
                      <button onClick={() => togglePromo(p.id)}
                        className={`flex items-center gap-1 flex-1 justify-center py-1.5 text-xs font-semibold rounded-sm uppercase tracking-widest border ${
                          p.is_active ? "border-line text-dim hover:bg-panel-hover" : "border-buy text-buy hover:bg-green-900"
                        }`}>
                        {p.is_active ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                        {p.is_active ? t("disable") : t("enable") }
                      </button>
                      <button onClick={() => deletePromo(p.id)}
                        className="flex items-center gap-1 px-3 py-1.5 border border-sell text-sell text-xs rounded-sm hover:bg-red-900 uppercase tracking-widest">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ─── Payment Methods Tab ─── */}
        {tab === "payment_methods" && (
          <PaymentMethodsTab paymentMethods={paymentMethods} load={load} />
        )}

        {tab === "settings" && <SettingsPanel />}

        {/* ─── Audit Logs Tab ─── */}
        {tab === "audit-logs" && <AuditLogsTab logs={auditLogs} setLogs={setAuditLogs} />}

        {/* ─── Financial Reports Tab ─── */}
        {tab === "reports" && <FinancialReportsTab report={financialReport} setReport={setFinancialReport} period={reportPeriod} setPeriod={setReportPeriod} />}

        {/* ─── User Detail Modal ─── */}
        {userDetail && (
          <ModalBase open={true} onClose={() => setUserDetail(null)} title={t("user_detail")} icon={UserCheck} maxW="max-w-3xl">
            <UserDetailModal data={userDetail} onClose={() => setUserDetail(null)} />
          </ModalBase>
        )}
          </div>
        </div>
      </div>

      {/* ═══ MODALS ═══ */}

      {/* Reject KYC */}
      <InputModal
        open={modal?.type === "rejectKyc"} onClose={closeModal} onSubmit={execRejectKyc}
        title={t("kyc_reject")} icon={XCircle}
        confirmLabel={t("reject")} confirmColor="bg-sell text-white hover:bg-red-500"
        fields={[{ key: "reason", label: t("reject_reason"), placeholder: t("insufficient_docs"), default: t("insufficient_docs") }]}
      />

      {/* Set Role */}
      <InputModal
        open={modal?.type === "setRole"} onClose={closeModal} onSubmit={execSetRole}
        title={t("change_role")} icon={Shield}
        confirmLabel={t("apply")} confirmColor="bg-buy text-black hover:bg-emerald-400"
        fields={[{ key: "role", label: t("new_role"), type: "select", options: [{ value: "user", label: t("user_role") }, { value: "admin", label: t("admin_role") }], default: modal?.currentRole === "admin" ? "user" : "admin" }]}
      />

      {/* Balance Adjustment */}
      <InputModal
        open={modal?.type === "balance"} onClose={closeModal} onSubmit={execBalance}
        title={t("adjust_balance")} icon={DollarSign}
        confirmLabel={t("apply")} confirmColor="bg-buy text-black hover:bg-emerald-400"
        fields={[
          { key: "mode", label: t("account"), type: "select", options: [{ value: "real", label: t("real_account") }, { value: "demo", label: t("demo_account") }], default: "real" },
          { key: "amount", label: t("amount") + " (negatif = düşme)", type: "number", placeholder: "100", default: "" },
        ]}
      />

      {/* Ban User */}
      <InputModal
        open={modal?.type === "ban"} onClose={closeModal} onSubmit={execBan}
        title={t("block_user")} icon={Ban}
        confirmLabel={t("ban")} confirmColor="bg-sell text-white hover:bg-red-500"
        fields={[
          { key: "reason", label: t("reason"), placeholder: t("suspicious_activity"), default: t("suspicious_activity") },
          { key: "duration_days", label: t("ban_duration_hint"), type: "number", placeholder: t("permanent_ban_hint"), default: "" },
        ]}
      />

      {/* Unban Confirm */}
      <ConfirmModal
        open={modal?.type === "unban"} onClose={closeModal} onConfirm={execUnban}
        title={t("unban")} icon={Unlock}
        message={t("unban_confirm")}
        confirmLabel={t("unban")} confirmColor="bg-buy text-black hover:bg-emerald-400"
      />

      {/* Set Limits */}
      <InputModal
        open={modal?.type === "limits"} onClose={closeModal} onSubmit={execLimits}
        title={t("trade_limits")} icon={SlidersHorizontal}
        confirmLabel={t("save")} confirmColor="bg-buy text-black hover:bg-emerald-400"
        fields={[
          { key: "max_trade_amount", label: t("max_trade_amount_hint"), type: "number", placeholder: t("unlimited"), default: modal?.limits?.max_trade_amount || "" },
          { key: "max_daily_trades", label: t("max_daily_trades_hint"), type: "number", placeholder: t("unlimited"), default: modal?.limits?.max_daily_trades || "" },
          { key: "max_daily_withdrawal", label: t("max_daily_withdrawal_hint"), type: "number", placeholder: t("unlimited"), default: modal?.limits?.max_daily_withdrawal || "" },
        ]}
      />

      {/* Reply Ticket */}
      <InputModal
        open={modal?.type === "reply"} onClose={closeModal} onSubmit={execReply}
        title={t("support_reply")} icon={Send}
        confirmLabel={t("send")} confirmColor="bg-buy text-black hover:bg-emerald-400"
        fields={[{ key: "message", label: t("reply_message"), type: "textarea", placeholder: t("reply_placeholder"), default: "" }]}
      />

      {/* Close Ticket Confirm */}
      <ConfirmModal
        open={modal?.type === "closeTicket"} onClose={closeModal} onConfirm={execCloseTicket}
        title={t("close_ticket")} icon={X}
        message={t("close_ticket_confirm")}
        confirmLabel={t("close")} confirmColor="bg-sell text-white hover:bg-red-500"
      />

      {/* Delete Promo Confirm */}
      <ConfirmModal
        open={modal?.type === "deletePromo"} onClose={closeModal} onConfirm={execDeletePromo}
        title={t("delete_promo")} icon={Trash2}
        message={t("delete_promo_confirm")}
        confirmLabel={t("delete")} confirmColor="bg-sell text-white hover:bg-red-500"
      />
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
//  DASHBOARD TAB
// ═══════════════════════════════════════════════════════════
function DashboardTab({ analytics }) {
  const { t } = useI18n();
  if (!analytics) return <div className="text-dim text-sm text-center py-12">Analitik veriler yükleniyor...</div>;
  const { days, summary } = analytics;
  const chartData = days.map(d => ({ ...d, date: d.date.slice(5) }));
  const summaryCards = [
    { label: t("total_trade_volume"), value: `$${summary.total_volume.toLocaleString()}`, icon: TrendingUp, color: "#00e676" },
    { label: t("platform_revenue"), value: `$${summary.total_earnings.toLocaleString()}`, icon: DollarSign, color: "#ff9800" },
    { label: t("total_trades"), value: summary.total_trades.toLocaleString(), icon: Activity, color: "#448aff" },
    { label: t("total_deposit"), value: `$${summary.total_deposits.toLocaleString()}`, icon: ArrowDownToLine, color: "#a855f7" },
    { label: t("total_withdrawal"), value: `$${summary.total_withdrawals.toLocaleString()}`, icon: ArrowUpFromLine, color: "#ef4444" },
    { label: t("new_users"), value: summary.total_new_users.toLocaleString(), icon: UserPlus, color: "#06b6d4" },
  ];
  const tooltipStyle = { backgroundColor: "rgba(22,27,40,0.95)", border: "1px solid rgba(45,53,72,0.8)", borderRadius: "6px", color: "#c8d3e8", fontSize: "11px", fontFamily: "'JetBrains Mono', monospace" };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {summaryCards.map((card) => (
          <div key={card.label} className="bg-panel border border-line rounded-sm p-4 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-12 h-12 opacity-5"><card.icon className="w-full h-full" /></div>
            <div className="text-[9px] uppercase tracking-widest text-dim mb-2">{card.label}</div>
            <div className="text-lg sm:text-xl font-bold font-mono" style={{ color: card.color }}>{card.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-panel border border-line rounded-sm p-4 sm:p-6">
        <div className="text-xs uppercase tracking-widest text-dim font-bold mb-4 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-buy" /> İşlem Hacmi & Platform Kazancı (Son 30 Gün)
        </div>
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00e676" stopOpacity={0.3} /><stop offset="95%" stopColor="#00e676" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="earnGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ff9800" stopOpacity={0.3} /><stop offset="95%" stopColor="#ff9800" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#64748b" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 9, fill: "#64748b" }} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} />
              <RTooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Area type="monotone" dataKey="volume" name={t("trade_volume")} stroke="#00e676" fill="url(#volGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="earnings" name={t("revenue")} stroke="#ff9800" fill="url(#earnGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-panel border border-line rounded-sm p-4 sm:p-6">
          <div className="text-xs uppercase tracking-widest text-dim font-bold mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4" style={{ color: "#448aff" }} /> Günlük İşlem Sayısı
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#64748b" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 9, fill: "#64748b" }} tickLine={false} axisLine={false} />
                <RTooltip contentStyle={tooltipStyle} />
                <Bar dataKey="trades" name={t("trade_single")} fill="#448aff" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-panel border border-line rounded-sm p-4 sm:p-6">
          <div className="text-xs uppercase tracking-widest text-dim font-bold mb-4 flex items-center gap-2">
            <UserPlus className="w-4 h-4" style={{ color: "#06b6d4" }} /> Aktif & Yeni Kullanıcılar
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#64748b" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 9, fill: "#64748b" }} tickLine={false} axisLine={false} />
                <RTooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="active_users" name={t("active")} fill="#06b6d4" radius={[3, 3, 0, 0]} />
                <Bar dataKey="new_users" name={t("new")} fill="#a855f7" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-panel border border-line rounded-sm p-4 sm:p-6">
        <div className="text-xs uppercase tracking-widest text-dim font-bold mb-4 flex items-center gap-2">
          <DollarSign className="w-4 h-4" style={{ color: "#a855f7" }} /> Yatırım & Çekim Akışı
        </div>
        <div style={{ width: "100%", height: 250 }}>
          <ResponsiveContainer>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="depGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3} /><stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="withGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} /><stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#64748b" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 9, fill: "#64748b" }} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} />
              <RTooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Area type="monotone" dataKey="deposits" name={t("deposit")} stroke="#a855f7" fill="url(#depGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="withdrawals" name={t("withdrawal")} stroke="#ef4444" fill="url(#withGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
//  TRADES TAB (NEW)
// ═══════════════════════════════════════════════════════════
function TradesTab() {
  const { t } = useI18n();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "", trade_type: "", symbol: "", user_email: "" });
  const [forceClosing, setForceClosing] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.trade_type) params.trade_type = filters.trade_type;
      if (filters.symbol) params.symbol = filters.symbol;
      if (filters.user_email) params.user_email = filters.user_email;
      params.limit = 200;
      const { data } = await api.get("/admin/trades", { params });
      setTrades(Array.isArray(data) ? data : []);
    } catch (e) { setTrades([]); toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const forceClose = async (id) => {
    if (!window.confirm) return;
    setForceClosing(id);
    try {
      const { data } = await api.post(`/admin/trades/${id}/force-close`);
      toast.success(`${t("cfd_force_closed")} $${data.close_price?.toFixed(4)} · PnL: ${data.pnl >= 0 ? "+" : ""}$${data.pnl?.toFixed(2)}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setForceClosing(null); }
  };

  const openCount = trades.filter(t => t.status === "open").length;
  const cfdCount = trades.filter(t => t.type === "cfd").length;
  const totalVol = trades.reduce((s, t) => s + (t.amount || 0), 0);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: t("showing"), value: trades.length, color: "#c8d3e8" },
          { label: t("open_status"),       value: openCount,     color: "var(--iq-green)" },
          { label: t("total_volume"), value: `$${totalVol.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, color: "#ff9800" },
        ].map(c => (
          <div key={c.label} className="bg-panel border border-line rounded-sm px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest text-dim mb-1">{c.label}</div>
            <div className="text-xl font-bold font-mono" style={{ color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Filter Bar */}
      <div className="bg-panel border border-line rounded-sm p-3">
        <div className="flex items-center justify-between mb-2">
          <button onClick={() => setShowFilters(f => !f)} className="flex items-center gap-2 text-xs text-dim hover:text-white uppercase tracking-widest">
            <Filter className="w-3.5 h-3.5" /> Filtreler {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          <CsvButton endpoint="/admin/export/trades" filename="trades.csv" />
          <button onClick={load} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-buy text-black text-[10px] font-bold rounded-sm hover:bg-emerald-400 uppercase tracking-widest disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Yenile
          </button>
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t border-subtle">
            {[
              { key: "status",     label: t("status"),   type: "select", options: [{ v: "", l: t("all") }, { v: "open", l: t("open_status") }, { v: "closed", l: t("closed") }, { v: "expired", l: t("expired") }] },
              { key: "trade_type", label: t("type"),     type: "select", options: [{ v: "", l: t("all") }, { v: "binary", l: t("binary") }, { v: "cfd", l: "CFD" }] },
              { key: "symbol",     label: t("symbol"),  type: "text",   placeholder: "BTCUSDT" },
              { key: "user_email", label: t("email"), type: "text",   placeholder: "user@..." },
            ].map(f => (
              <div key={f.key}>
                <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">{f.label}</label>
                {f.type === "select" ? (
                  <select value={filters[f.key]} onChange={e => setFilters(v => ({ ...v, [f.key]: e.target.value }))}
                    className="w-full bg-app border border-line rounded-sm px-2 py-1.5 text-xs outline-none focus:border-buy">
                    {f.options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                ) : (
                  <input value={filters[f.key]} onChange={e => setFilters(v => ({ ...v, [f.key]: e.target.value }))}
                    onKeyDown={e => e.key === t("enter") && load()}
                    placeholder={f.placeholder}
                    className="w-full bg-app border border-line rounded-sm px-2 py-1.5 text-xs outline-none focus:border-buy font-mono" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-panel border border-line rounded-sm overflow-x-auto">
        <table className="w-full text-xs font-mono min-w-[900px]">
          <thead className="bg-app border-b border-line">
            <tr className="text-dim uppercase tracking-widest text-[10px]">
              <th className="text-left px-2 py-1.5">Kullanıcı</th>
              <th className="text-left px-2 py-1.5 hidden md:table-cell">Tür</th>
              <th className="text-left px-2 py-1.5">Sembol</th>
              <th className="text-left px-2 py-1.5">Yön</th>
              <th className="text-right px-2 py-1.5">Tutar</th>
              <th className="text-right px-2 py-1.5 hidden md:table-cell">Kaldıraç</th>
              <th className="text-right px-2 py-1.5 hidden md:table-cell">Giriş</th>
              <th className="text-right px-2 py-1.5">PnL</th>
              <th className="text-left px-2 py-1.5">Durum</th>
              <th className="text-left px-2 py-1.5 hidden md:table-cell">Açılış</th>
              <th className="text-right px-2 py-1.5">{t("action_header")}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="text-center py-12 text-dim">Yükleniyor...</td></tr>
            )}
            {!loading && trades.length === 0 && (
              <tr><td colSpan={11} className="text-center py-12 text-dim">İşlem bulunamadı</td></tr>
            )}
            {trades.map(tr => {
              const isOpen = tr.status === "open";
              const isCfd = tr.type === "cfd";
              const pnl = tr.pnl;
              const isBuy = tr.direction === "call" || tr.direction === "buy";
              return (
                <tr key={tr.id} className="border-b border-subtle hover:bg-panel-hover">
                  <td className="px-2 py-1.5 truncate max-w-[100px] md:max-w-[140px]">{tr.user_email}</td>
                  <td className="px-2 py-1.5 hidden md:table-cell">
                    <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${
                      isCfd ? "border-blue-400 text-blue-400" : "border-orange-400 text-orange-400"
                    }`}>{tr.type}</span>
                  </td>
                  <td className="px-2 py-1.5 text-white">{tr.symbol}</td>
                  <td className="px-2 py-1.5">
                    <span className={`flex items-center gap-0.5 ${isBuy ? "text-buy" : "text-sell"}`}>
                      {isBuy ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {tr.direction}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-right">${Number(tr.amount).toFixed(2)}</td>
                  <td className="px-2 py-1.5 text-right text-dim hidden md:table-cell">{tr.leverage}×</td>
                  <td className="px-2 py-1.5 text-right hidden md:table-cell">{Number(tr.entry_price).toFixed(4)}</td>
                  <td className={`px-2 py-1.5 text-right font-bold ${pnl == null ? "text-dim" : pnl >= 0 ? "text-buy" : "text-sell"}`}>
                    {pnl == null ? "—" : `${pnl >= 0 ? "+" : ""}$${Number(pnl).toFixed(2)}`}
                  </td>
                  <td className="px-2 py-1.5">
                    <span className={`text-[9px] uppercase tracking-widest ${
                      isOpen ? "text-buy" : tr.status === "expired" ? "text-orange-400" : "text-dim"
                    }`}>
                      {isOpen ? "● Açık" : tr.status === "expired" ? t("expired") : t("closed") }
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-dim whitespace-nowrap hidden md:table-cell">{new Date(tr.opened_at).toLocaleString("tr-TR")}</td>
                  <td className="px-2 py-1.5 text-right">
                    {isOpen && isCfd ? (
                      <button
                        onClick={() => forceClose(tr.id)}
                        disabled={forceClosing === tr.id}
                        className="flex items-center gap-1 px-1.5 py-1 border border-sell text-sell text-[9px] rounded-sm hover:bg-red-900 uppercase tracking-widest disabled:opacity-50"
                      >
                        <Zap className="w-2.5 h-2.5" />
                        {forceClosing === tr.id ? "..." : t("close") }
                      </button>
                    ) : (
                      <span className="text-dim text-[10px]">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
//  TX HISTORY TAB (NEW)
// ═══════════════════════════════════════════════════════════
function TxHistoryTab() {
  const { t } = useI18n();
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "", tx_type: "", user_email: "" });
  const [showFilters, setShowFilters] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (filters.status) params.status = filters.status;
      if (filters.tx_type) params.tx_type = filters.tx_type;
      if (filters.user_email) params.user_email = filters.user_email;
      const { data } = await api.get("/admin/transactions/history", { params });
      setTxs(Array.isArray(data) ? data : []);
    } catch (e) { setTxs([]); toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const totalDeposits = txs.filter(t => t.type === "deposit" && t.status === "completed").reduce((s, t) => s + t.amount, 0);
  const totalWithdrawals = txs.filter(t => t.type === "withdraw" && t.status === "completed").reduce((s, t) => s + t.amount, 0);

  const statusColor = (s) => s === "completed" ? "text-buy" : s === "rejected" ? "text-sell" : "text-orange-400";
  const statusLabel = (s) => s === "completed" ? t("completed") : s === "rejected" ? t("rejected") : t("pending");
  const typeColor = (tp) => tp === "deposit" ? "#00e676" : tp === "withdraw" ? "#ef4444" : "#448aff";
  const typeLabel = (tp) => tp === "deposit" ? t("deposit") : tp === "withdraw" ? t("withdrawal") : t("adjustment");

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: t("total_records"), value: txs.length, color: "#c8d3e8" },
          { label: t("total_deposit"), value: `$${totalDeposits.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, color: "var(--iq-green)" },
          { label: t("total_withdrawal"), value: `$${totalWithdrawals.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, color: "#ef4444" },
        ].map(c => (
          <div key={c.label} className="bg-panel border border-line rounded-sm px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest text-dim mb-1">{c.label}</div>
            <div className="text-xl font-bold font-mono" style={{ color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Filter Bar */}
      <div className="bg-panel border border-line rounded-sm p-3">
        <div className="flex items-center justify-between mb-2">
          <button onClick={() => setShowFilters(f => !f)} className="flex items-center gap-2 text-xs text-dim hover:text-white uppercase tracking-widest">
            <Filter className="w-3.5 h-3.5" /> Filtreler {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          <CsvButton endpoint="/admin/export/transactions" filename="transactions.csv" />
          <button onClick={load} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-buy text-black text-[10px] font-bold rounded-sm hover:bg-emerald-400 uppercase tracking-widest disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Yenile
          </button>
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 pt-2 border-t border-subtle">
            {[
              { key: "status",     label: t("status"), type: "select", options: [{ v: "", l: t("all") }, { v: "pending", l: t("pending") }, { v: "completed", l: t("completed") }, { v: "rejected", l: t("rejected") }] },
              { key: "tx_type",    label: t("type"),   type: "select", options: [{ v: "", l: t("all") }, { v: "deposit", l: t("deposit") }, { v: "withdraw", l: t("withdrawal") }, { v: "adjustment", l: t("adjustment") }] },
              { key: "user_email", label: t("email"), type: "text", placeholder: "user@..." },
            ].map(f => (
              <div key={f.key}>
                <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">{f.label}</label>
                {f.type === "select" ? (
                  <select value={filters[f.key]} onChange={e => setFilters(v => ({ ...v, [f.key]: e.target.value }))}
                    className="w-full bg-app border border-line rounded-sm px-2 py-1.5 text-xs outline-none focus:border-buy">
                    {f.options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                ) : (
                  <input value={filters[f.key]} onChange={e => setFilters(v => ({ ...v, [f.key]: e.target.value }))}
                    onKeyDown={e => e.key === t("enter") && load()}
                    placeholder={f.placeholder}
                    className="w-full bg-app border border-line rounded-sm px-2 py-1.5 text-xs outline-none focus:border-buy font-mono" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-panel border border-line rounded-sm overflow-x-auto">
        <table className="w-full text-xs font-mono min-w-[800px]">
          <thead className="bg-app border-b border-line">
            <tr className="text-dim uppercase tracking-widest text-[10px]">
              <th className="text-left px-2 py-1.5">Tarih</th>
              <th className="text-left px-2 py-1.5">Kullanıcı</th>
              <th className="text-left px-2 py-1.5">Tür</th>
              <th className="text-right px-2 py-1.5">Tutar</th>
              <th className="text-left px-2 py-1.5 hidden md:table-cell">Network</th>
              <th className="text-left px-2 py-1.5">Durum</th>
              <th className="text-left px-2 py-1.5 hidden md:table-cell">Not / Hash</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-12 text-dim">Yükleniyor...</td></tr>}
            {!loading && txs.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-dim">İşlem bulunamadı</td></tr>}
            {txs.map(tx => (
              <tr key={tx.id} className="border-b border-subtle hover:bg-panel-hover">
                <td className="px-2 py-1.5 text-muted whitespace-nowrap">{new Date(tx.created_at).toLocaleString("tr-TR")}</td>
                <td className="px-2 py-1.5 truncate max-w-[100px] md:max-w-[160px]">{tx.user_email}</td>
                <td className="px-2 py-1.5">
                  <span style={{ fontSize: 9, color: typeColor(tx.type), background: `${typeColor(tx.type)}22`, border: `1px solid ${typeColor(tx.type)}44`, borderRadius: 3, padding: "2px 4px", fontWeight: 700, textTransform: "uppercase" }}>
                    {typeLabel(tx.type)}
                  </span>
                </td>
                <td className={`px-2 py-1.5 text-right font-bold ${tx.type === "deposit" ? "text-buy" : tx.type === "withdraw" ? "text-sell" : "text-dim"}`}>
                  {tx.type === "withdraw" ? "-" : tx.type === "deposit" ? "+" : ""}${Number(tx.amount).toFixed(2)}
                </td>
                <td className="px-2 py-1.5 text-dim hidden md:table-cell">{tx.network || "—"}</td>
                <td className={`px-2 py-1.5 ${statusColor(tx.status)}`}>
                  <span className="text-[9px] uppercase tracking-widest">{statusLabel(tx.status)}</span>
                </td>
                <td className="px-2 py-1.5 text-dim truncate max-w-[200px] hidden md:table-cell">
                  {tx.note || tx.tx_hash || tx.address || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
//  NOTIFICATIONS TAB
// ═══════════════════════════════════════════════════════════
function NotificationsTab({ users }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ title: "", message: "", target_user_id: "" });
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [searchQ, setSearchQ] = useState("");

  const loadHistory = async () => {
    try { const { data } = await api.get("/admin/notifications?limit=30"); setHistory(data); } catch {}
  };
  useEffect(() => { loadHistory(); }, []);

  const send = async (broadcast) => {
    if (!form.title.trim() || !form.message.trim()) { toast.error(t("toast_title_message_required")); return; }
    if (!broadcast && !form.target_user_id) { toast.error(t("toast_target_user_required")); return; }
    setSending(true);
    try {
      const payload = { title: form.title, message: form.message, target_user_id: broadcast ? null : form.target_user_id };
      const { data } = await api.post("/admin/notifications/send", payload);
      toast.success(t("notification_sent", { count: data.sent_to }));
      setForm({ title: "", message: "", target_user_id: "" });
      loadHistory();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const filteredUsers = users.filter(u =>
    !searchQ || u.email.toLowerCase().includes(searchQ.toLowerCase()) || (u.name || "").toLowerCase().includes(searchQ.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="bg-panel border border-line rounded-sm p-5">
        <div className="font-semibold flex items-center gap-2 mb-4"><Bell className="w-4 h-4 text-buy" /> Bildirim Gönder</div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">{t("title_label")}</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy" placeholder={t("notification_title_placeholder")} />
          </div>
          <div>
            <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">Mesaj *</label>
            <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              rows={3} className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy resize-none" placeholder="Bildirim mesajı..." />
          </div>
          <div>
            <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">{t("target_user_label")}</label>
            <input value={searchQ} onChange={e => setSearchQ(e.target.value)}
              className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy mb-2" placeholder="Email ile ara..." />
            {searchQ && (
              <div className="max-h-32 overflow-y-auto bg-app border border-line rounded-sm">
                {filteredUsers.slice(0, 8).map(u => (
                  <button key={u.id} onClick={() => { setForm(f => ({ ...f, target_user_id: u.id })); setSearchQ(u.email); }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-panel-hover transition-colors flex justify-between ${form.target_user_id === u.id ? "bg-panel-hover text-buy" : "text-muted"}`}>
                    <span>{u.email}</span><span className="text-dim">{u.name || ""}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={() => send(false)} disabled={sending}
              className="flex items-center gap-2 px-4 py-2 bg-buy text-black text-xs font-bold rounded-sm hover:bg-emerald-400 uppercase tracking-widest disabled:opacity-50">
              <Send className="w-3.5 h-3.5" /> Bireysel Gönder
            </button>
            <button onClick={() => send(true)} disabled={sending}
              className="flex items-center gap-2 px-4 py-2 border border-orange-400 text-orange-400 text-xs font-bold rounded-sm hover:bg-orange-400/10 uppercase tracking-widest disabled:opacity-50">
              <Bell className="w-3.5 h-3.5" /> Toplu Gönder
            </button>
          </div>
        </div>
      </div>

      <div className="bg-panel border border-line rounded-sm p-5">
        <div className="font-semibold flex items-center gap-2 mb-4"><Eye className="w-4 h-4 text-dim" /> Son Gönderilen Bildirimler</div>
        {history.length === 0 ? (
          <div className="text-center py-8 text-dim text-sm">{t("no_notifications")}</div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {history.slice(0, 20).map(n => (
              <div key={n.id} className="flex items-start gap-3 p-3 bg-app border border-subtle rounded-sm">
                <div className={`w-2 h-2 mt-1.5 rounded-full flex-shrink-0 ${n.type === "broadcast" ? "bg-orange-400" : "bg-buy"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <div className="font-semibold text-sm truncate">{n.title}</div>
                    <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border flex-shrink-0 ml-2 ${
                      n.type === "broadcast" ? "border-orange-400 text-orange-400" : "border-buy text-buy"
                    }`}>{n.type === "broadcast" ? t("broadcast") : t("individual") }</span>
                  </div>
                  <div className="text-xs text-muted mt-1 truncate">{n.message}</div>
                  <div className="text-[10px] text-dim mt-1">{n.sent_by} · {new Date(n.created_at).toLocaleString("tr-TR")}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
//  USERS TAB
// ═══════════════════════════════════════════════════════════
function UsersTab({ users, userQ, setUserQ, load, isSuperadmin, setUserRole, adjustBalance, banUser, unbanUser, setUserLimits, setUserDetail }) {
  const { t } = useI18n();
  const [expandedUser, setExpandedUser] = useState(null);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center gap-2 bg-panel border border-line rounded-sm px-3 py-2 flex-1 max-w-md">
          <Search className="w-4 h-4 text-dim" />
          <input data-testid="user-search" value={userQ} onChange={(e) => setUserQ(e.target.value)}
            onKeyDown={(e) => e.key === t("enter") && load()}
            placeholder="Search email or name…" className="flex-1 bg-transparent outline-none text-sm" />
          <button onClick={load} data-testid="user-search-btn" className="text-xs uppercase tracking-widest text-buy hover:underline">Search</button>
        </div>
        <div className="text-xs text-dim ml-auto font-mono">
          <CsvButton endpoint="/admin/export/users" filename="users.csv" />
        </div>
      </div>
      <div className="bg-panel border border-line rounded-sm overflow-x-auto" data-testid="users-table">
        <table className="w-full text-xs font-mono min-w-[1000px]">
          <thead className="bg-app border-b border-line">
            <tr className="text-dim uppercase tracking-widest text-[10px]">
              <th className="text-left px-2 py-1.5">Email</th>
              <th className="text-left px-2 py-1.5 hidden md:table-cell">Name</th>
              <th className="text-left px-2 py-1.5 hidden md:table-cell">Role</th>
              <th className="text-left px-2 py-1.5">Durum</th>
              <th className="text-right px-2 py-1.5">Demo</th>
              <th className="text-right px-2 py-1.5">Real</th>
              <th className="text-left px-2 py-1.5 hidden md:table-cell">KYC</th>
              <th className="text-left px-2 py-1.5 hidden md:table-cell">Limitler</th>
              <th className="text-left px-2 py-1.5 hidden md:table-cell">Joined</th>
              <th className="text-right px-2 py-1.5">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-dim">No users</td></tr>}
            {users.map((u) => {
              const isSuper = u.role === "superadmin";
              const isAdmin = u.role === "admin";
              const isBanned = u.status === "banned";
              const isSuspended = u.status === "suspended";
              const hasLimits = u.limits && Object.keys(u.limits).length > 0;
              const isExpanded = expandedUser === u.id;
              return (
                <React.Fragment key={u.id}>
                  <tr data-testid={`user-row-${u.id}`} className={`border-b border-subtle hover:bg-panel-hover ${(isBanned || isSuspended) ? "opacity-70" : ""}`}>
                    <td className="px-2 py-1.5 truncate max-w-[100px] md:max-w-none">{u.email}</td>
                    <td className="px-2 py-1.5 text-muted hidden md:table-cell">{u.name || "—"}</td>
                    <td className="px-2 py-1.5 hidden md:table-cell">
                      <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${
                        isSuper ? "border-orange-400 text-orange-400" : isAdmin ? "border-buy text-buy" : "border-line text-dim"
                      }`}>
                        {isSuper && <ShieldCheck className="w-2.5 h-2.5" />}
                        {isAdmin && <Shield className="w-2.5 h-2.5" />}
                        {u.role}
                      </span>
                    </td>
                    <td className="px-2 py-1.5">
                      {isBanned ? (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-red-500 text-red-500 bg-red-500/10">
                          <Ban className="w-2.5 h-2.5" /> Engelli
                        </span>
                      ) : isSuspended ? (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-orange-400 text-orange-400 bg-orange-400/10">
                          <Ban className="w-2.5 h-2.5" /> Askıda
                        </span>
                      ) : (
                        <span className="text-[9px] uppercase tracking-widest text-buy">{t("active")}</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">${u.demo_balance.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">${u.real_balance.toFixed(2)}</td>
                    <td className={`px-2 py-1.5 uppercase hidden md:table-cell text-[9px] ${u.kyc_status === "approved" ? "text-buy" : u.kyc_status === "pending" ? "text-orange-400" : "text-dim"}`}>{u.kyc_status}</td>
                    <td className="px-2 py-1.5 hidden md:table-cell">
                      {hasLimits ? (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-orange-400 text-orange-400">
                          <SlidersHorizontal className="w-2.5 h-2.5" /> Kısıtlı
                        </span>
                      ) : <span className="text-[9px] text-dim">—</span>}
                    </td>
                    <td className="px-2 py-1.5 text-dim whitespace-nowrap hidden md:table-cell">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-2 py-1.5 text-right">
                      {isSuper ? (
                        <span className="text-dim text-[9px]">protected</span>
                      ) : (
                        <div className="inline-flex gap-1">
                          <button onClick={() => setExpandedUser(isExpanded ? null : u.id)}
                            className="px-2 py-1 border border-line rounded-sm hover:bg-panel-hover text-[10px] uppercase tracking-widest" title={t("detail")}>
                            <Eye className="w-3 h-3 inline" />
                          </button>
                          <button onClick={() => setUserDetail(u.id)}
                            className="px-2 py-1 border border-blue-400 text-blue-400 rounded-sm hover:bg-blue-900 text-[10px] uppercase tracking-widest" title={t("full_profile")}>
                            <UserCheck className="w-3 h-3 inline" />
                          </button>
                          {(isBanned || isSuspended) ? (
                            <button onClick={() => unbanUser(u.id, u.email)}
                              className="px-2 py-1 border border-buy text-buy rounded-sm hover:bg-green-900 text-[10px] uppercase tracking-widest" title={t("unban")}>
                              <Unlock className="w-3 h-3 inline" />
                            </button>
                          ) : (
                            <button onClick={() => banUser(u.id, u.email)}
                              className="px-2 py-1 border border-sell text-sell rounded-sm hover:bg-red-900 text-[10px] uppercase tracking-widest" title={t("ban")}>
                              <Ban className="w-3 h-3 inline" />
                            </button>
                          )}
                          <button onClick={() => setUserLimits(u.id, u.limits)}
                            className="px-2 py-1 border border-line rounded-sm hover:bg-panel-hover text-[10px] uppercase tracking-widest" title={t("set_limits")}>
                            <SlidersHorizontal className="w-3 h-3 inline" />
                          </button>
                          {!u.email_verified && (
                            <button onClick={async () => { try { await api.post(`/admin/users/${u.id}/verify-email`); toast.success(t("toast_email_verified_admin")); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }}
                              className="px-2 py-1 border border-buy text-buy rounded-sm hover:bg-green-900 text-[10px] uppercase tracking-widest" title={t("verify_email")}>
                              <CheckCircle2 className="w-3 h-3 inline" />
                            </button>
                          )}
                          {!isSuper && (
                            <>
                              {isAdmin
                                ? <button data-testid={`demote-${u.id}`} onClick={() => setUserRole(u.id, u.role)} className="px-2 py-1 border border-line rounded-sm hover:bg-panel-hover text-[10px] uppercase tracking-widest">Demote</button>
                                : <button data-testid={`promote-${u.id}`} onClick={() => setUserRole(u.id, u.role)} className="px-2 py-1 border border-line rounded-sm hover:bg-panel-hover text-[10px] uppercase tracking-widest">{t("admin_header")}</button>}
                              <button data-testid={`adjust-${u.id}`} onClick={() => adjustBalance(u.id)} className="px-2 py-1 border border-line rounded-sm hover:bg-panel-hover text-[10px] uppercase tracking-widest">± $</button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={10} className="px-4 py-3 bg-app border-b border-line">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                          <div>
                            <div className="text-[10px] text-dim uppercase tracking-widest mb-1">Durum Detayı</div>
                            {(isBanned || isSuspended) ? (
                              <div>
                                <div className="text-sell font-semibold">{isBanned ? t("permanent_ban") : t("temporary_suspension") }</div>
                                {u.ban_reason && <div className="text-muted mt-1">Sebep: {u.ban_reason}</div>}
                                {u.ban_until && <div className="text-muted">Bitiş: {new Date(u.ban_until).toLocaleDateString("tr-TR")}</div>}
                              </div>
                            ) : <div className="text-buy font-semibold">Aktif Kullanıcı</div>}
                          </div>
                          <div>
                            <div className="text-[10px] text-dim uppercase tracking-widest mb-1">İşlem Limitleri</div>
                            {hasLimits ? (
                              <div className="space-y-0.5">
                                {u.limits.max_trade_amount && <div>Maks İşlem: <span className="text-orange-400">${u.limits.max_trade_amount}</span></div>}
                                {u.limits.max_daily_trades && <div>Günlük İşlem: <span className="text-orange-400">{u.limits.max_daily_trades}</span></div>}
                                {u.limits.max_daily_withdrawal && <div>Günlük Çekim: <span className="text-orange-400">${u.limits.max_daily_withdrawal}</span></div>}
                              </div>
                            ) : <div className="text-muted">Sınırsız</div>}
                          </div>
                          <div>
                            <div className="text-[10px] text-dim uppercase tracking-widest mb-1">Güvenlik</div>
                            <div>2FA: {u.totp_enabled ? <span className="text-buy">{t("active")}</span> : <span className="text-dim">Kapalı</span>}</div>
                            <div>KYC: <span className={u.kyc_status === "approved" ? "text-buy" : "text-dim"}>{u.kyc_status}</span></div>
                            <div className="flex items-center gap-1">
                              E-posta:
                              {u.email_verified ? (
                                <span className="text-buy">Onaylı</span>
                              ) : (
                                <>
                                  <span className="text-sell">Onaysız</span>
                                  <button
                                    onClick={async () => {
                                      try {
                                        await api.post(`/admin/users/${u.id}/verify-email`);
                                        toast.success(t("toast_email_verified_admin"));
                                        load();
                                      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                                    }}
                                    className="ml-1 px-1.5 py-0.5 border border-buy text-buy rounded-sm hover:bg-green-900 text-[9px] uppercase tracking-widest"
                                  >Onayla</button>
                                </>
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] text-dim uppercase tracking-widest mb-1">{t("referral")}</div>
                            <div className="text-muted">{u.referral_code || "—"}</div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ─── Promo Creation Form ───
function PromoForm({ onCreated, onClose }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ title: "", description: "", promo_type: "bonus", value: 100, min_deposit: 0, max_uses: "", expires_at: "", is_active: true });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!form.title || !form.description || !form.value) { toast.error(t("toast_required_fields")); return; }
    setLoading(true);
    try {
      await api.post("/support/admin/promos", {
        ...form,
        value: Number(form.value),
        min_deposit: Number(form.min_deposit) || 0,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_at: form.expires_at || null,
      });
      toast.success(t("toast_promo_created"));
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-panel border border-line rounded-sm p-5 mb-4">
      <div className="flex justify-between items-center mb-4">
        <div className="font-semibold flex items-center gap-2"><Gift className="w-4 h-4 text-buy" /> Yeni Promosyon Oluştur</div>
        <button onClick={onClose}><X className="w-4 h-4 text-dim" /></button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">{t("title_label")}</label>
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy" placeholder={t("promo_title_placeholder")} />
        </div>
        <div className="col-span-2">
          <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">{t("description")} *</label>
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            rows={2} className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy resize-none" placeholder={t("promo_desc_placeholder")} />
        </div>
        <div>
          <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">Tür *</label>
          <select value={form.promo_type} onChange={e => setForm(f => ({ ...f, promo_type: e.target.value }))}
            className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy">
            <option value="bonus">Bonus ($)</option>
            <option value="cashback">Cashback (%)</option>
            <option value="free_trade">{t("free_trade_opt")}</option>
            <option value="deposit_match">{t("deposit_match_opt")}</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">{t("value_label")}</label>
          <input type="number" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
            className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy font-mono" />
        </div>
        <div>
          <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">{t("min_deposit_label")}</label>
          <input type="number" value={form.min_deposit} onChange={e => setForm(f => ({ ...f, min_deposit: e.target.value }))}
            className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy font-mono" />
        </div>
        <div>
          <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">{t("max_usage_label")}</label>
          <input type="number" value={form.max_uses} onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))}
            placeholder={t("unlimited")} className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy font-mono" />
        </div>
        <div className="col-span-2">
          <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">{t("expiry_date_label")}</label>
          <input type="date" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
            className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy" />
        </div>
        <div className="col-span-2 flex items-center gap-3">
          <label className="text-[10px] text-dim uppercase tracking-widest">{t("active")}</label>
          <button onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))} style={{ color: form.is_active ? "var(--iq-green)" : "var(--iq-text-dim)" }}>
            {form.is_active ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
          </button>
        </div>
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-line rounded-sm text-xs text-dim hover:bg-panel-hover uppercase tracking-widest">{t("cancel")}</button>
          <button onClick={submit} disabled={loading}
            className="px-6 py-2 bg-buy text-black rounded-sm text-xs font-bold hover:bg-emerald-400 uppercase tracking-widest flex items-center gap-2" style={{ opacity: loading ? 0.6 : 1 }}>
            <Plus className="w-3.5 h-3.5" /> Oluştur
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Shared small components ───
const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-panel p-4">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-buy" />
      <div className="text-[10px] uppercase tracking-widest text-dim">{label}</div>
    </div>
    <div className="font-mono text-xl sm:text-2xl font-bold" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>{value}</div>
  </div>
);

const TxTable = ({ rows, onApprove, onReject, testid, showHash, showAddress }) => (
  <div className="bg-panel border border-line rounded-sm overflow-x-auto" data-testid={testid}>
    <table className="w-full text-xs font-mono min-w-[700px]">
      <thead className="bg-app border-b border-line">
        <tr className="text-dim uppercase tracking-widest text-[10px]">
          <th className="text-left px-3 py-2">Date</th>
          <th className="text-left px-2 py-2">User</th>
          <th className="text-right px-2 py-2">Amount</th>
          <th className="text-left px-2 py-2">Network</th>
          {showHash && <th className="text-left px-2 py-2">Tx Hash</th>}
          {showAddress && <th className="text-left px-2 py-2">Address</th>}
          <th className="text-right px-3 py-2">Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={showHash || showAddress ? 6 : 5} className="text-center py-8 text-dim">No pending items</td></tr>}
        {rows.map((r) => (
          <tr key={r.id} data-testid={`tx-row-${r.id}`} className="border-b border-subtle hover:bg-panel-hover">
            <td className="px-3 py-2 text-muted whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
            <td className="px-2 py-2">{r.user_email}</td>
            <td className="px-2 py-2 text-right text-white">${Number(r.amount).toFixed(2)}</td>
            <td className="px-2 py-2 text-muted">{r.network}</td>
            {showHash && <td className="px-2 py-2 text-dim truncate max-w-[200px]">{r.tx_hash}</td>}
            {showAddress && <td className="px-2 py-2 text-dim truncate max-w-[200px]">{r.address}</td>}
            <td className="px-3 py-2 text-right">
              <div className="inline-flex gap-1">
                <button data-testid={`tx-approve-${r.id}`} onClick={() => onApprove(r.id)} className="px-2 py-1 bg-buy text-black rounded-sm hover:bg-emerald-400 text-[10px] uppercase tracking-widest font-semibold">Approve</button>
                <button data-testid={`tx-reject-${r.id}`} onClick={() => onReject(r.id)} className="px-2 py-1 bg-sell text-white rounded-sm hover:bg-red-500 text-[10px] uppercase tracking-widest font-semibold">Reject</button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);


// ─── Settings Panel ───
function SettingsPanel() {
  const { t } = useI18n();
  const { settings, updateSettings, loading } = useSettings();
  const [form, setForm] = useState({ site_title: "", footer_text: "", logo_base64: null, favicon_base64: null });
  const [smtpForm, setSmtpForm] = useState({ smtp_host: "", smtp_port: 587, smtp_user: "", smtp_password: "", smtp_from: "" });
  const [saving, setSaving] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [smtpLoading, setSmtpLoading] = useState(true);

  useEffect(() => {
    if (settings) {
      setForm({
        site_title: settings.site_title || "",
        footer_text: settings.footer_text || "",
        logo_base64: settings.logo_base64 || null,
        favicon_base64: settings.favicon_base64 || null,
      });
    }
  }, [settings]);

  // Load SMTP settings from admin API
  const loadSmtpSettings = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setSmtpForm({
        smtp_host: data.smtp_host || "",
        smtp_port: data.smtp_port || 587,
        smtp_user: data.smtp_user || "",
        smtp_password: "", // Never load password from server
        smtp_from: data.smtp_from || "",
      });
    } catch (e) {
      console.error("Failed to load SMTP settings", e);
    } finally {
      setSmtpLoading(false);
    }
  };

  useEffect(() => {
    loadSmtpSettings();
  }, []);

  const handleFile = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error(t("toast_file_size_limit")); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, [field]: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try { await updateSettings(form); toast.success(t("toast_settings_saved")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveSmtp = async () => {
    setSavingSmtp(true);
    try {
      await api.put("/admin/settings/smtp", {
        smtp_host: smtpForm.smtp_host,
        smtp_port: Number(smtpForm.smtp_port),
        smtp_user: smtpForm.smtp_user,
        smtp_password: smtpForm.smtp_password,
        smtp_from: smtpForm.smtp_from,
      });
      toast.success(t("toast_smtp_saved"));
      // Clear password field after save
      setSmtpForm(f => ({ ...f, smtp_password: "" }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingSmtp(false);
    }
  };

  if (loading || smtpLoading) return <div className="text-dim text-sm py-12 text-center">Yükleniyor...</div>;

  return (
    <div className="space-y-6">
      <div className="bg-panel border border-line rounded-sm p-6 max-w-2xl">
        <h2 className="text-lg font-bold mb-6 flex items-center gap-2"><Settings className="w-5 h-5 text-buy" /> Site Genel Ayarları</h2>
        <div className="space-y-5">
          <div>
            <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">Site Başlığı (Title)</label>
            <input value={form.site_title} onChange={e => setForm(f => ({ ...f, site_title: e.target.value }))}
              className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy" placeholder="IQTrade" />
          </div>
          <div>
            <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">Footer Metni</label>
            <input value={form.footer_text} onChange={e => setForm(f => ({ ...f, footer_text: e.target.value }))}
              className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy" placeholder="IQ Trade · Educational Simulator" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] text-dim uppercase tracking-widest block mb-2">Site Logosu (Navbar)</label>
              <div className="border border-dashed border-line rounded-sm p-4 text-center">
                {form.logo_base64 ? (
                  <div className="relative inline-block">
                    <img src={form.logo_base64} alt={t("logo")} className="max-h-16 mx-auto mb-2" />
                    <button onClick={() => setForm(f => ({ ...f, logo_base64: null }))} className="absolute -top-2 -right-2 bg-sell text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ) : <div className="text-xs text-dim mb-2">Logo Yok</div>}
                <input type="file" accept="image/*" onChange={(e) => handleFile(e, "logo_base64")}
                  className="block w-full text-[10px] text-muted file:mr-2 file:py-1 file:px-2 file:rounded-sm file:border-0 file:text-[10px] file:bg-panel-hover file:text-white hover:file:bg-line cursor-pointer" />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-dim uppercase tracking-widest block mb-2">Favicon (Tarayıcı Sekmesi)</label>
              <div className="border border-dashed border-line rounded-sm p-4 text-center">
                {form.favicon_base64 ? (
                  <div className="relative inline-block">
                    <img src={form.favicon_base64} alt={t("favicon")} className="w-8 h-8 object-contain mx-auto mb-2" />
                    <button onClick={() => setForm(f => ({ ...f, favicon_base64: null }))} className="absolute -top-2 -right-2 bg-sell text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ) : <div className="text-xs text-dim mb-2">Favicon Yok</div>}
                <input type="file" accept="image/*" onChange={(e) => handleFile(e, "favicon_base64")}
                  className="block w-full text-[10px] text-muted file:mr-2 file:py-1 file:px-2 file:rounded-sm file:border-0 file:text-[10px] file:bg-panel-hover file:text-white hover:file:bg-line cursor-pointer" />
              </div>
            </div>
          </div>
          <div className="flex justify-end pt-4 border-t border-line">
            <button onClick={save} disabled={saving} className="px-6 py-2 bg-buy text-black rounded-sm text-xs font-bold hover:bg-emerald-400 uppercase tracking-widest disabled:opacity-50">
              {saving ? t("saving") : t("save_settings") }
            </button>
          </div>
        </div>
      </div>

      {/* SMTP Settings Section */}
      <div className="bg-panel border border-line rounded-sm p-6 max-w-2xl">
        <h2 className="text-lg font-bold mb-6 flex items-center gap-2"><Settings className="w-5 h-5 text-buy" /> E-posta (SMTP) Ayarları</h2>
        <div className="space-y-5">
          <div>
            <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">SMTP Sunucusu</label>
            <input value={smtpForm.smtp_host} onChange={e => setSmtpForm(f => ({ ...f, smtp_host: e.target.value }))}
              className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy" placeholder="smtp.gmail.com" />
          </div>
          <div>
            <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">SMTP Port</label>
            <input type="number" value={smtpForm.smtp_port} onChange={e => setSmtpForm(f => ({ ...f, smtp_port: Number(e.target.value) }))}
              className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy font-mono" placeholder="587" />
          </div>
          <div>
            <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">SMTP Kullanıcı Adı</label>
            <input value={smtpForm.smtp_user} onChange={e => setSmtpForm(f => ({ ...f, smtp_user: e.target.value }))}
              className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy" placeholder="you@gmail.com" />
          </div>
          <div>
            <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">SMTP Şifresi</label>
            <input type="password" value={smtpForm.smtp_password} onChange={e => setSmtpForm(f => ({ ...f, smtp_password: e.target.value }))}
              className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy font-mono" placeholder="Uygulama şifresi (Gmail için)" />
          </div>
          <div>
            <label className="text-[10px] text-dim uppercase tracking-widest block mb-1">Gönderen E-posta</label>
            <input value={smtpForm.smtp_from} onChange={e => setSmtpForm(f => ({ ...f, smtp_from: e.target.value }))}
              className="w-full bg-app border border-line rounded-sm px-3 py-2 text-sm outline-none focus:border-buy" placeholder="noreply@yourdomain.com" />
          </div>
          <div className="flex justify-end pt-4 border-t border-line">
            <button onClick={saveSmtp} disabled={savingSmtp} className="px-6 py-2 bg-buy text-black rounded-sm text-xs font-bold hover:bg-emerald-400 uppercase tracking-widest disabled:opacity-50">
              {savingSmtp ? t("saving") : t("save_smtp") }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  PAYMENT METHODS TAB (NEW)
// ═══════════════════════════════════════════════════════════
function PaymentMethodsTab({ paymentMethods, load }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ code: "USDT-TRC20", label: "", address: "" });
  const [loading, setLoading] = useState(false);

  const addMethod = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/payment-methods", form);
      toast.success(t("toast_deposit_method_added"));
      setForm({ code: "USDT-TRC20", label: "", address: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const deleteMethod = async (id) => {
    if (!window.confirm(t("confirm_delete_method"))) return;
    try {
      await api.delete(`/admin/payment-methods/${id}`);
      toast.success(t("toast_deposit_method_deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Form */}
      <div className="bg-panel border border-line rounded-sm p-4 h-fit">
        <h2 className="text-sm font-semibold mb-4 uppercase tracking-widest">Yeni Yöntem Ekle</h2>
        <form onSubmit={addMethod} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-dim block mb-1">Ağ Kodu (örn: USDT-TRC20)</label>
            <input required value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
              placeholder="USDT-TRC20" className="w-full bg-app border border-line rounded-sm px-3 py-2 text-xs outline-none focus:border-buy font-mono uppercase" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-dim block mb-1">Başlık (örn: Tether (TRC20))</label>
            <input required value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              placeholder="Tether (TRC20)" className="w-full bg-app border border-line rounded-sm px-3 py-2 text-xs outline-none focus:border-buy" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-dim block mb-1">Cüzdan Adresi</label>
            <input required value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
              placeholder="T..." className="w-full bg-app border border-line rounded-sm px-3 py-2 text-xs outline-none focus:border-buy font-mono" />
          </div>
          <button type="submit" disabled={loading} className="w-full bg-buy text-black font-bold py-2 rounded-sm hover:bg-emerald-400 uppercase tracking-widest text-xs disabled:opacity-50">
            {loading ? t("adding") : t("add") }
          </button>
        </form>
      </div>

      {/* List */}
      <div className="md:col-span-2 space-y-3">
        <h2 className="text-sm font-semibold mb-4 uppercase tracking-widest">Mevcut Yöntemler</h2>
        {paymentMethods.length === 0 && <div className="text-dim text-sm text-center py-8 bg-panel border border-line rounded-sm">Henüz yöntem eklenmemiş.</div>}
        {paymentMethods.map(pm => (
          <div key={pm.id} className="bg-panel border border-line rounded-sm p-4 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-sm">{pm.label}</span>
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-line rounded-sm text-dim">{pm.code}</span>
              </div>
              <div className="font-mono text-xs text-muted break-all">{pm.address}</div>
            </div>
            <button onClick={() => deleteMethod(pm.id)} className="px-3 py-1.5 border border-sell text-sell text-xs rounded-sm hover:bg-red-900 uppercase tracking-widest flex items-center gap-1 shrink-0">
              <Trash2 className="w-3 h-3" /> Sil
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
//  CSV DOWNLOAD HELPER
// ═══════════════════════════════════════════════════════════
function CsvButton({ endpoint, filename, label }) {
  const handleDownload = async () => {
    try {
      const { data } = await api.get(endpoint, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("toast_csv_download_failed"));
    }
  };
  return (
    <button onClick={handleDownload} className="inline-flex items-center gap-1 px-3 py-1.5 border border-line rounded-sm hover:bg-panel-hover text-[10px] uppercase tracking-widest">
      <Download className="w-3 h-3" /> {label}
    </button>
  );
}


// ═══════════════════════════════════════════════════════════
//  AUDIT LOGS TAB
// ═══════════════════════════════════════════════════════════
function AuditLogsTab({ logs, setLogs }) {
  const { t } = useI18n();
  const [actionFilter, setActionFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (actionFilter) params.action = actionFilter;
      const { data } = await api.get("/admin/audit-logs", { params });
      setLogs(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [actionFilter, setLogs]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}
          className="bg-panel border border-line px-3 py-2 rounded-sm text-xs outline-none">
          <option value="">{t("all_transactions")}</option>
          <option value="approve_tx">{t("approve_tx")}</option>
          <option value="reject_tx">{t("reject_tx")}</option>
          <option value="approve_kyc">{t("approve_kyc")}</option>
          <option value="reject_kyc">{t("reject_kyc")}</option>
          <option value="ban_user">{t("ban_user")}</option>
          <option value="unban_user">{t("unban_user")}</option>
          <option value="set_role">{t("change_role")}</option>
          <option value="bulk_ban">{t("bulk_ban")}</option>
          <option value="kyc_resubmit">{t("kyc_resubmit")}</option>
        </select>
        <button onClick={loadLogs} className="px-3 py-2 border border-line rounded-sm hover:bg-panel-hover text-xs">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      <div className="bg-panel border border-line rounded-sm overflow-x-auto">
        <table className="w-full text-xs font-mono min-w-[700px]">
          <thead className="bg-app border-b border-line">
            <tr className="text-dim uppercase tracking-widest text-[10px]">
              <th className="text-left px-3 py-2">{t("admin_header")}</th>
              <th className="text-left px-3 py-2">{t("action_header")}</th>
              <th className="text-left px-3 py-2 hidden md:table-cell">{t("target_header")}</th>
              <th className="text-left px-3 py-2 hidden md:table-cell">{t("detail_header")}</th>
              <th className="text-left px-3 py-2">Zaman</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-dim">{t("no_logs")}</td></tr>}
            {logs.map((l) => (
              <tr key={l.id} className="border-b border-subtle hover:bg-panel-hover">
                <td className="px-3 py-2 text-muted">{l.admin_email}</td>
                <td className="px-3 py-2">
                  <span className="px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-widest border border-line bg-app">{l.action}</span>
                </td>
                <td className="px-3 py-2 text-dim hidden md:table-cell">{l.target_type} {l.target_id}</td>
                <td className="px-3 py-2 text-dim hidden md:table-cell truncate max-w-[200px]">{l.details}</td>
                <td className="px-3 py-2 text-dim">{l.timestamp ? new Date(l.timestamp).toLocaleString("tr-TR") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
//  FINANCIAL REPORTS TAB
// ═══════════════════════════════════════════════════════════
function FinancialReportsTab({ report, setReport, period, setPeriod }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reports/financial", { params: { period } });
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [period, setReport]);

  useEffect(() => { loadReport(); }, [loadReport]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="flex gap-1">
          {["daily", "weekly", "monthly"].map((p) => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-widest rounded-sm border ${period === p ? "border-buy bg-buy-dim text-buy" : "border-line text-muted hover:bg-panel-hover"}`}>
              {p === "daily" ? t("daily") : p === "weekly" ? t("weekly") : t("monthly") }
            </button>
          ))}
        </div>
        <button onClick={loadReport} className="px-3 py-1.5 border border-line rounded-sm hover:bg-panel-hover text-xs">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {report && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-panel border border-line rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-widest text-dim mb-1">İşlem Sayısı</div>
              <div className="text-xl font-bold font-mono">{report.trade_count}</div>
            </div>
            <div className="bg-panel border border-line rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-widest text-dim mb-1">Toplam Hacim</div>
              <div className="text-xl font-bold font-mono">${report.total_volume}</div>
            </div>
            <div className="bg-panel border border-line rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-widest text-dim mb-1">Platform Kârı</div>
              <div className="text-xl font-bold font-mono text-buy">${report.total_platform_profit}</div>
            </div>
            <div className="bg-panel border border-line rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-widest text-dim mb-1">Kullanıcı Kârı</div>
              <div className="text-xl font-bold font-mono text-sell">${report.total_user_profit}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-panel border border-line rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-widest text-dim mb-1">Toplam Yatırım</div>
              <div className="text-lg font-bold font-mono text-buy">${report.total_deposits}</div>
            </div>
            <div className="bg-panel border border-line rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-widest text-dim mb-1">Toplam Çekim</div>
              <div className="text-lg font-bold font-mono text-sell">${report.total_withdrawals}</div>
            </div>
            <div className="bg-panel border border-line rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-widest text-dim mb-1">Net Akış</div>
              <div className="text-lg font-bold font-mono">${report.net_flow}</div>
            </div>
          </div>

          {report.symbol_breakdown && Object.keys(report.symbol_breakdown).length > 0 && (
            <div className="bg-panel border border-line rounded-sm p-4">
              <h3 className="text-sm font-bold mb-3">Varlık Bazlı Dağılım</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(report.symbol_breakdown).sort((a,b) => b[1].volume - a[1].volume).map(([sym, s]) => (
                  <div key={sym} className="bg-app border border-subtle rounded-sm px-3 py-2">
                    <div className="text-xs font-bold">{sym}</div>
                    <div className="text-[10px] text-dim">{s.count} işlem • ${s.volume.toFixed(0)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
//  USER DETAIL MODAL
// ═══════════════════════════════════════════════════════════
function UserDetailModal({ data, onClose }) {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let active = true;
    api.get(`/admin/users/${data}/detail`).then(({ data: d }) => {
      if (active) setDetail(d);
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
    .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [data]);

  if (loading) return <div className="text-center py-8 text-dim">Yükleniyor...</div>;
  if (!detail) return <div className="text-center py-8 text-sell">Veri yüklenemedi</div>;

  const u = detail.user;
  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      <div className="grid grid-cols-2 gap-3">
        <div><span className="text-[10px] text-dim uppercase">E-posta</span><div className="text-sm font-mono">{u.email}</div></div>
        <div><span className="text-[10px] text-dim uppercase">İsim</span><div className="text-sm font-mono">{u.name}</div></div>
        <div><span className="text-[10px] text-dim uppercase">Rol</span><div className="text-sm font-mono">{u.role}</div></div>
        <div><span className="text-[10px] text-dim uppercase">Mod</span><div className="text-sm font-mono">{u.account_mode}</div></div>
        <div><span className="text-[10px] text-dim uppercase">Demo Bakiye</span><div className="text-sm font-mono">${u.demo_balance}</div></div>
        <div><span className="text-[10px] text-dim uppercase">Gerçek Bakiye</span><div className="text-sm font-mono">${u.real_balance}</div></div>
        <div><span className="text-[10px] text-dim uppercase">KYC</span><div className="text-sm font-mono">{u.kyc_status}</div></div>
        <div><span className="text-[10px] text-dim uppercase">Durum</span><div className="text-sm font-mono">{u.status}</div></div>
      </div>

      <div>
        <h4 className="text-xs font-bold uppercase tracking-widest text-buy mb-2">Son İşlemler ({detail.trades.length})</h4>
        <div className="max-h-40 overflow-y-auto">
          {detail.trades.map((t) => (
            <div key={t.id} className="flex justify-between bg-app border border-subtle rounded-sm px-2 py-1.5 mb-1 text-[10px] font-mono">
              <span>{t.symbol} {t.type} {t.direction}</span>
              <span>${t.amount}</span>
              <span className={t.pnl >= 0 ? "text-buy" : "text-sell"}>{t.pnl !== null && t.pnl !== undefined ? `$${t.pnl}` : t.status}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h4 className="text-xs font-bold uppercase tracking-widest text-purple-400 mb-2">Son Finansal İşlemler ({detail.transactions.length})</h4>
        <div className="max-h-32 overflow-y-auto">
          {detail.transactions.map((t) => (
            <div key={t.id} className="flex justify-between bg-app border border-subtle rounded-sm px-2 py-1.5 mb-1 text-[10px] font-mono">
              <span>{t.type}</span><span>${t.amount}</span><span>{t.status}</span>
            </div>
          ))}
        </div>
      </div>

      {detail.support_tickets.length > 0 && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-widest text-yellow-500 mb-2">Destek Talepleri ({detail.support_tickets.length})</h4>
          {detail.support_tickets.map((t) => (
            <div key={t.id} className="bg-app border border-subtle rounded-sm px-2 py-1.5 mb-1 text-[10px] font-mono">
              {t.subject} — {t.status}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
